import { Injectable } from '@angular/core';
import { Product } from '../../../shared/models/product.model';
import { Ga4Item, toGa4ItemFromProduct } from './ga4-items';

/**
 * GA4 rejects oversized payloads, so a long category listing is truncated rather than
 * dropped. `view_item_list` is not a conversion event — `purchase` carries the complete
 * basket — so the cap costs nothing that the ecommerce reports depend on.
 */
const MAX_ITEMS_PER_EVENT = 100;

const CURRENCY = 'EUR';

function roundToCents(value: number): number {
  return parseFloat(value.toFixed(2));
}

@Injectable({ providedIn: 'root' })
export class AnalyticsService {
  trackEvent(eventName: string, params?: Record<string, unknown>): void {
    if (typeof window.gtag !== 'function') return;
    window.gtag('event', eventName, params);
  }

  trackFileDownload(
    linkUrl: string,
    extraParams?: Record<string, unknown>
  ): void {
    const fileName = this.extractFileName(linkUrl);
    this.trackEvent('file_download', {
      file_name: fileName,
      file_extension: this.extractFileExtension(fileName),
      link_url: linkUrl,
      ...extraParams,
    });
  }

  trackViewItem(product: Product): void {
    if (!product) return;
    const item = toGa4ItemFromProduct(product);
    this.trackEvent('view_item', {
      currency: CURRENCY,
      value: roundToCents(item.price * item.quantity),
      items: [item],
    });
  }

  trackViewItemList(itemListName: string, products: Array<Product>): void {
    if (!products?.length) return;
    this.trackEvent('view_item_list', {
      item_list_name: itemListName,
      items: products
        .slice(0, MAX_ITEMS_PER_EVENT)
        .map((product, index) => ({ ...toGa4ItemFromProduct(product), index })),
    });
  }

  trackSelectItem(itemListName: string, product?: Product): void {
    const items: Ga4Item[] = product ? [toGa4ItemFromProduct(product)] : [];
    this.trackEvent('select_item', {
      item_list_name: itemListName,
      ...(items.length ? { items } : {}),
    });
  }

  trackAddToCart(
    product: Product,
    quantity: number,
    extraParams?: Record<string, unknown>
  ): void {
    if (!product) return;
    const item = toGa4ItemFromProduct(product, quantity);
    this.trackEvent('add_to_cart', {
      currency: CURRENCY,
      value: roundToCents(item.price * item.quantity),
      items: [item],
      ...extraParams,
    });
  }

  private extractFileName(url: string): string {
    const withoutQuery = url.split('?')[0];
    return withoutQuery.substring(withoutQuery.lastIndexOf('/') + 1);
  }

  private extractFileExtension(fileName: string): string {
    const dotIndex = fileName.lastIndexOf('.');
    return dotIndex === -1 ? '' : fileName.substring(dotIndex + 1);
  }
}
